/** Tracks current and maximum HP for an entity, emitting events on damage, healing, and death. */
import Phaser from 'phaser';
import { Component } from './Component';

/** Encapsulates HP state and emits health-changed and entity-died events through the shared entity EventEmitter. */
export class HealthComponent implements Component {
  private currentHp: number;
  private maxHp: number;
  private events: Phaser.Events.EventEmitter;

  constructor(events: Phaser.Events.EventEmitter, maxHp: number) {
    this.maxHp = maxHp;
    this.currentHp = maxHp;
    this.events = events;
  }

  get hp(): number {
    return this.currentHp;
  }

  get max(): number {
    return this.maxHp;
  }

  get ratio(): number {
    return this.currentHp / this.maxHp;
  }

  get isDead(): boolean {
    return this.currentHp <= 0;
  }

  /** Reduce HP by amount, clamping to zero, and emit health-changed and entity-died as appropriate. */
  takeDamage(amount: number): void {
    this.currentHp = Math.max(0, this.currentHp - amount);
    this.events.emit('health-changed', this.currentHp, this.maxHp);
    if (this.isDead) {
      this.events.emit('entity-died');
    }
  }

  /** Restore HP by amount, clamping to max, and emit health-changed. */
  heal(amount: number): void {
    this.currentHp = Math.min(this.maxHp, this.currentHp + amount);
    this.events.emit('health-changed', this.currentHp, this.maxHp);
  }

  /** Update maximum HP, preserving the current HP ratio, and emit health-changed. */
  setMaxHp(newMax: number): void {
    const ratio = this.currentHp / this.maxHp;
    this.maxHp = newMax;
    this.currentHp = Math.round(ratio * newMax);
    this.events.emit('health-changed', this.currentHp, this.maxHp);
  }

  /** Set HP to an exact value, clamping to [0, max], and emit health-changed (+ entity-died if HP reaches 0). */
  setHp(value: number): void {
    this.currentHp = Math.max(0, Math.min(this.maxHp, value));
    this.events.emit('health-changed', this.currentHp, this.maxHp);
    if (this.isDead) {
      this.events.emit('entity-died');
    }
  }

  update(_delta: number): void {
    // No per-frame health logic needed
  }

  destroy(): void {
    // Nothing to clean up
  }
}
